/**
 * @file This is the connection service module
 * @copyright Andreas Horvath 2016-2024
 * @version 0.2.0
 */
import { AuthService } from '../auth/auth.service';
import { MaintenanceService } from '../maintenance/maintenance.service';
import { retryReadOnce, withDeadline } from '../maintenance/failure-diagnostics';
import { Injectable } from '@angular/core';
import { AuthBackendService } from '../../backend-services';
import { MyPlayerService } from 'src/app/modules/game/services/my-player.service';
import { GameShellService } from 'src/app/modules/game/services/game-shell.service';
import { LobbyService } from 'src/app/modules/game/services/lobby.service';
import { InfoDialogService } from 'src/app/modules/game/services/info-dialog.service';
import { SocketIoService } from '../socket-io/socket-io.service';
import { GameStateService } from 'src/app/modules/game/services/game-state.service';
import { OnlineStatusEnum } from '../../enums';
import { MdbToastService } from '../mdb-toast/mdb-toast.service';
import { RetentionLiveService } from '../retention/retention-live.service';
import { ConnectionLostService } from 'src/app/modules/game/services/connection-lost.service';
import { GameEventService } from 'src/app/modules/game/services/game-event.service';

declare var io: any;

@Injectable({
  providedIn: 'root',
})
export class ConnectionService {
  private readonly SEND_PING_INTERVAL = 20000;

  // how long the client has to answer ping with pong before it is considered bad connection
  private readonly PING_TIMEOUT_BAD_CONNECTION = 1000; // if the client takes more than 1 second, it IS slow.

  private readonly LAST_ABSENCE_BACK_EFFECTIVE_TIME = 3000; // 3 sec after pressing "Im back" we want it to be effective still
  private _lastAbsenceBackCallAt: number | undefined = undefined;

  private hadConnection = false;
  private connectionSequence = 0;

  private _lastPingTime = 0;
  private _lastPongTime = 0;
  private _lastHearbeatTime = 0; // how long the last heartbeat took
  private _heartbeatTimes: number[] = [];
  private _badConnection = false;

  // Inactive opponent leave message tracking
  private _inactiveOpponentCheckInterval: any = null;
  private _inactiveOpponentCheckCount = 0;
  private _lastCanLeaveMessageTime = 0;
  private readonly MIN_MESSAGE_INTERVAL = 90000; // 90 seconds minimum between messages

  constructor(
    private authBackendService: AuthBackendService,
    private authService: AuthService,
    private infoDialogService: InfoDialogService,
    private myPlayer: MyPlayerService,
    private lobbyService: LobbyService,
    private gameShellService: GameShellService,
    private socketIO: SocketIoService,
    private state: GameStateService,
    private toastService: MdbToastService,
    private connectionLostService: ConnectionLostService,
    private gameEventService: GameEventService,
    private maintenance: MaintenanceService,
    private retentionLive: RetentionLiveService
  ) {
    this.hadConnection = this.socketIO.connected;
    console.log('Constructor of connection service called.');

    this.socketIO.on('connect', () => this._onConnect());
    this.socketIO.on('disconnect', (reason) => this._onDisconnect(reason));
    this.socketIO.on('reconnect_attempt', (attempt) =>
      this._onReconnectAttempt(attempt)
    );
    this.socketIO.on('reconnect_error', (err) => this._onReconnectError(err));
    this.socketIO.on('connectionLost', (data) =>
      this._playerDisconnected(data.data)
    );
    this.socketIO.on('reEntered', (data) => this._playerReconnected(data.data));
    this.socketIO.on('didAutoAction', (data) =>
      this._playerDidNotReact(data.data)
    );
    this.socketIO.on('userDidAction', (data) =>
      this._playerDidReact(data.data)
    );
    this.socketIO.on('reloadPage', () => location.reload());
    this.socketIO.on('logoff', (message) => { void this.authService.handleServerLogout(message?.data?.reason); });
    this.socketIO.on('logout', (message) => { void this.authService.handleServerLogout(message?.data?.reason); });
    this.socketIO.on('socketDisconnectNewWSID', () => {
      this._socketDisconnectNewWSID();
    });
    this.socketIO.on('communityActivity', (data) =>
      this._onCommunityActivity(data)
    );
    // Retention toasts (tasks, daily bonus) live in RetentionLiveService; injecting it
    // here registers its socket listeners once at app start.

    // Listen to game events to clean up inactive opponent check
    this.gameEventService.gameEvent$.subscribe((event) => {
      if (event.name === 'gameEnd' || event.name === 'leftTable') {
        this._stopInactiveOpponentCheck();
      }
    });

    // io.socket._raw.io.engine._callbacks.$ping.push(() => this._pingHandler());
    // io.socket._raw.io.engine._callbacks.$pong.push(() => this._pongHandler());

    setInterval(() => {
      this._sendPing();
    }, this.SEND_PING_INTERVAL);

    // A background window gets its timers throttled (the ping above then goes out about once
    // a minute). When the window comes back after a while, ping right away instead of waiting.
    document.addEventListener('visibilitychange', () => this._onVisibilityChange());
  }

  private _hiddenSince: number | undefined;
  private readonly PING_ON_RETURN_AFTER_HIDDEN_MS = 30000;

  private _onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      this._hiddenSince = Date.now();
      return;
    }
    const hiddenFor = this._hiddenSince ? Date.now() - this._hiddenSince : 0;
    this._hiddenSince = undefined;
    if (hiddenFor >= this.PING_ON_RETURN_AFTER_HIDDEN_MS && this.socketIO.connected) {
      this._sendPing().catch(() => undefined);
    }
  }

  public async absenceBack(): Promise<void> {
    try {
      await this.socketIO.post('connection/absence-back', {});
      this.myPlayer.player.onlineStatus = OnlineStatusEnum.ONLINE;
      this._lastAbsenceBackCallAt = Date.now();
    } catch (error) {
      console.error('Error calling absence-back:', error);
    }
  }

  /**
   * checks if io.socket is ocnnected
   * @returns {Boolean}
   */
  public get connected() {
    return io.socket.isConnected();
  }

  /**
   * checks if connection is bad (when ping-pong takes very long)
   * @returns {Boolean}
   */
  public get hasBadConnection() {
    return this._badConnection;
  }

  /**
   * gets last heartbeat time
   * @returns {Number}
   */
  public get lastHearbeatTime() {
    return this._lastHearbeatTime;
  }

  private async _sendPing() {
    await this.socketIO.post('connection/ping');
  }

  /**
   * called when _pongHandler notices that i got a bad connection
   * @param {Number} lastHeartbeat
   */
  async _gotBadConnection(lastHeartbeat) {
    console.log(
      `It seems like your connection is not very good, your last Heartbeat took ${lastHeartbeat} ms.`
    );
  }

  /**
   * called when i lose connection
   * @param {Object} reason
   */
  private async _onDisconnect(reason) {
    this.connectionSequence++;
    console.log('WebSocket got disconnected from server. Reason: ' + reason);
    // Clean up inactive opponent check when disconnected
    this._stopInactiveOpponentCheck();
  }

  /**
   * called when i reconnect
   */
  private async _onReconnect(sequence: number) {
    // connect fires after every successful handshake; Socket.IO 4 emits `reconnect`
    // on its Manager, not on the socket exposed by the Sails wrapper.
    await retryReadOnce(() => this.myPlayer.update());
    if (sequence !== this.connectionSequence) return;
    // By the server's state, not the view: a gameStart sent while the socket was down is
    // lost (lobby on screen although my game runs), as is the table view after a second drop
    // during re-enter (refreshGameAfterDisconnect shows the lobby until the game is back).
    if (this.gameShellService.currentView === 'game' || this.gameShellService.seatedAtRunningTable) {
      await withDeadline(this.gameShellService.refreshGameAfterDisconnect(), 25000);
    } else {
      await withDeadline(this.lobbyService.update());
    }
    if (sequence !== this.connectionSequence) return;
    this.infoDialogService.hideInfoDialog(
      'Deine Verbindung wurde getrennt',
      'Es wird gerade versucht, eine Verbindung herzustellen..'
    );
  }

  /**
   * called when i do a reconnect attempt
   * @param {Number} attempt
   */
  private async _onReconnectAttempt(attempt) {
    console.log(
      `WebSocket trying to reconnect to server... (Attempt #${attempt})`
    );
  }

  /**
   * called when i do a reconnect attempt and encountered an error
   * @param {Object} err
   */
  private async _onReconnectError(err): Promise<void> {
    if (this.socketIO.connected) return;

    console.log(`WebSocket could not reconnect to server.`, err);

    this.infoDialogService.showInfoDialog(
      'Deine Verbindung wurde getrennt',
      'Es wird gerade versucht, eine Verbindung herzustellen..',
      -1
    );
  }

  /**
   * The server marked me offline (no ping for a minute), but my socket works: it just
   * delivered this message. Happens when the browser throttles the ping timer of a background
   * window (e.g. the game popup behind another window while waiting at a table). Closing the
   * connection here froze the game: cards on screen, no more events, every move played
   * automatically until a reload (3-player tables, 2026-11). Instead sign in again and fetch
   * the game, exactly like after a reconnect.
   */
  private async _serverDisconnectedMe(): Promise<void> {
    console.log('The server marked me offline although my socket works: restoring the session.');
    if (!this.socketIO.connected) return; // a real disconnect: the socket reconnects by itself

    const sequence = ++this.connectionSequence;
    const completeRecovery = this.gameShellService.hasLoaded
      ? this.maintenance.beginGameRecovery()
      : () => {};
    try {
      await this.socketIO.initializePlayer();
      if (sequence !== this.connectionSequence) return;
      await this._onReconnect(sequence);
    } catch (error) {
      console.warn('Could not restore the session after being marked offline:', error);
      if (sequence === this.connectionSequence) {
        this.maintenance.setServerError(error, 'RECONNECT');
      }
    } finally {
      completeRecovery();
    }
  }

  /**s
   * invoked when some player gets disconnected
   * @param {Object} data
   */
  private async _playerDisconnected(data: any): Promise<void> {
    console.log('_playerDisconnected', data);

    if (this.myPlayer.player.userID === data.userID)
      return await this._serverDisconnectedMe();
    if (
      !this.myPlayer.player.table ||
      this.gameShellService.currentView !== 'game'
    )
      return;

    const player =
      data.userID === this.myPlayer.player.userID
        ? this.myPlayer.player
        : this.state.players[
            this.state.players.map((el) => el.userID).indexOf(data.userID)
          ];

    if (player.onlineStatus !== OnlineStatusEnum.OFFLINE) {
      player.onlineStatus = OnlineStatusEnum.OFFLINE;

      this.infoDialogService.showInfoDialog(
        player.username,
        'hat die Verbindung verloren.'
      );
    }

    // Start checking if player can leave without penalty in 2-player games
    this._startInactiveOpponentCheck();
  }

  /**
   * invoked when some other player reconnects (reEntered)
   * @param {Object} data
   */
  private async _playerReconnected(data: any): Promise<void> {
    console.log('_playerReconnected', data);

    if (
      !this.myPlayer.player.table ||
      this.gameShellService.currentView !== 'game'
    )
      return;

    await this.state.updatePlayerInfosFromBackend();

    // Stop checking when opponent reconnects
    this._stopInactiveOpponentCheck();
  }

  /**
   * invoked when some other player did not react and the computer did an auto action
   * @param {Object} data
   */
  private async _playerDidNotReact(data: any): Promise<void> {
    console.log('_playerDidNotReact', data);

    if (
      !this.myPlayer.player.table ||
      this.gameShellService.currentView !== 'game'
    )
      return;

    const player =
      data.userID === this.myPlayer.player.userID
        ? this.myPlayer.player
        : this.state.players[
            this.state.players.map((el) => el.userID).indexOf(data.userID)
          ];

    if (
      data.userID !== this.myPlayer.player.userID ||
      !this._lastAbsenceBackCallAt ||
      Date.now() - this._lastAbsenceBackCallAt >
        this.LAST_ABSENCE_BACK_EFFECTIVE_TIME
    ) {
      if (player.onlineStatus !== OnlineStatusEnum.OFFLINE) {
        player.onlineStatus =
          data.isAboutToBeVeryInactive || data.isVeryInactive
            ? OnlineStatusEnum.UNRESPONSIVE
            : OnlineStatusEnum.NOT_REACTING;
      }
    }

    // Start checking if player can leave without penalty in 2-player games
    this._startInactiveOpponentCheck();
  }

  /**
   * invoked when some other player did an action by themselves (opposite of did not react)
   * @param {Object} data
   */
  private async _playerDidReact(data: any): Promise<void> {
    console.log('_playerDidReact', data);

    if (
      !this.myPlayer.player.table ||
      this.gameShellService.currentView !== 'game'
    )
      return;

    const player =
      data.userID === this.myPlayer.player.userID
        ? this.myPlayer.player
        : this.state.players[
            this.state.players.map((el) => el.userID).indexOf(data.userID)
          ];

    if (OnlineStatusEnum.checkForNonReactive(player.onlineStatus)) {
      player.onlineStatus = OnlineStatusEnum.ONLINE;
    }

    // Stop checking when opponent becomes active again
    if (data.userID !== this.myPlayer.player.userID)
      this._stopInactiveOpponentCheck();
  }

  /**
   * called on connect
   */
  private async _onConnect() {
    const sequence = ++this.connectionSequence;
    const resuming = this.hadConnection && this.gameShellService.hasLoaded &&
      !this.maintenance.gameStartupActive;
    this.hadConnection = true;
    console.log('WebSocket connected.');
    const completeRecovery = resuming ? this.maintenance.beginGameRecovery() : () => {};
    try {
      // Restore the server-side socket association before fetching/re-entering the game.
      await this.socketIO.initializePlayer();
      if (sequence !== this.connectionSequence) return;
      if (resuming) await this._onReconnect(sequence);
    } catch (error) {
      console.warn('Could not restore socket:', error);
      if (resuming && sequence === this.connectionSequence) {
        this.maintenance.setServerError(error, 'RECONNECT');
      }
    } finally {
      completeRecovery();
    }
  }

  private _socketDisconnectNewWSID() {
    this.connectionLostService.show();
    this._closeConnectionAndNeverReconnect();
  }

  /**
   * socket io ping handler
   */
  private _pingHandler() {
    this._lastPingTime = Date.now();
  }

  /**
   * socket io pong handler
   */
  private _pongHandler() {
    this._lastPongTime = Date.now();
    if (this._lastPingTime)
      this._lastHearbeatTime = this._lastPongTime - this._lastPingTime;

    if (this._lastHearbeatTime > this.PING_TIMEOUT_BAD_CONNECTION) {
      if (this._badConnection === false) {
        this._badConnection = true;
        this._gotBadConnection(this._lastHearbeatTime);
      }
    } else {
      this._badConnection = false;
    }

    this._heartbeatTimes.push(this._lastHearbeatTime);
  }

  /**
   * Handles community activity events, shows achievement toasts for own achievements only
   * @param {Object} data - WebSocket message data
   */
  private _onCommunityActivity(data: any): void {
    if (data?.data?.activityType === 'achievement_earned') {
      const { userID, activityData } = data.data;

      // Only show toast for my own achievements
      if (userID === this.myPlayer.player.userID) {
        this.toastService.success(
          `${activityData.achievementIcon} Du hast "${activityData.achievementName}" erreicht!`
        );
      }
    }
  }


  private _closeConnectionAndNeverReconnect() {
    console.log('Permanently closed connection.');
    io.socket.disconnect();
    io.socket.reconnection = false;
  }

  /**
   * Starts periodic check for inactive opponent in 2-player games
   * Shows a message when player can leave without penalty
   */
  private _startInactiveOpponentCheck(): void {
    // Only check in 2-player games (not 3 or 4 player)
    if (this.state.players.length !== 2) {
      return;
    }

    // Don't start multiple intervals
    if (this._inactiveOpponentCheckInterval) {
      return;
    }

    console.log('Starting inactive opponent check for 2-player game');

    // Check immediately, then every 30 seconds
    this._checkIfCanLeaveWithoutPenalty();
    this._inactiveOpponentCheckInterval = setInterval(() => {
      this._checkIfCanLeaveWithoutPenalty();
    }, 30000); // Check every 30 seconds
  }

  /**
   * Stops periodic check for inactive opponent
   */
  private _stopInactiveOpponentCheck(): void {
    if (this._inactiveOpponentCheckInterval) {
      console.log('Stopping inactive opponent check');
      clearInterval(this._inactiveOpponentCheckInterval);
      this._inactiveOpponentCheckInterval = null;
      this._inactiveOpponentCheckCount = 0;
      this._lastCanLeaveMessageTime = 0;
    }
  }

  /**
   * Checks if player can leave without penalty and shows message if yes
   */
  private async _checkIfCanLeaveWithoutPenalty(): Promise<void> {
    // Only check in 2-player games
    if (this.state.players.length !== 2) {
      this._stopInactiveOpponentCheck();
      return;
    }

    // Only check during active game
    if (
      !this.myPlayer.player.table ||
      this.gameShellService.currentView !== 'game' ||
      !this.state.isRoundRunning
    ) {
      return;
    }

    // Find opponent (the other player that is not me)
    const opponent = this.state.players.find(
      (player) => player.userID !== this.myPlayer.player.userID
    );

    if (!opponent) {
      return;
    }

    // Check if opponent is inactive (NOT_REACTING or OFFLINE)
    const isOpponentInactive = OnlineStatusEnum.checkIsNotActiveAndOnline(
      opponent.onlineStatus
    );

    if (!isOpponentInactive) {
      // Opponent is active again, stop checking
      this._stopInactiveOpponentCheck();
      return;
    }

    // Increment check counter
    this._inactiveOpponentCheckCount++;

    // Only show message after 3rd check (90 seconds) and then every 90-120 seconds
    const now = Date.now();
    const timeSinceLastMessage = now - this._lastCanLeaveMessageTime;

    // Don't show before 3rd check
    if (this._inactiveOpponentCheckCount < 3) {
      return;
    }

    // After 3rd check, only show if enough time has passed (90-120 seconds)
    if (
      this._lastCanLeaveMessageTime > 0 &&
      timeSinceLastMessage < this.MIN_MESSAGE_INTERVAL
    ) {
      return;
    }

    // Check with backend if leaving would cause punishment
    try {
      const shouldPunish = await this.socketIO.get('game/check-leave-punish');

      if (!shouldPunish) {
        // Player can leave without penalty - show message
        this._lastCanLeaveMessageTime = now;

        this.gameEventService.emit({
          name: 'canLeaveWithoutPenalty',
          data: {},
        });

        console.log(
          'Player can now leave without penalty due to inactive opponent'
        );
      }
    } catch (e) {
      console.warn('Error checking leave punishment:', e);
    }
  }
}
